// The active page and its neighbours share one translation. Preparing the
// neighbours before moving prevents an empty gutter, even when a drag is held.
export default function receivingTypeSwipe(element, {
  enabled, activeType, types, prepare, clear, select, nextTick,
}) {
  let gesture = null
  let frame = null
  let offset = 0
  let width = 0
  let revision = 0
  let preparing = null
  let prepared = false
  let running = false
  let settling = false
  let committing = false
  let runType = null
  let expectedType = null
  let requestedType = null
  let suppressClickUntil = 0
  const gestureListeners = []
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const removeGestureListeners = () => gestureListeners.forEach(([name, listener]) => window.removeEventListener(name, listener))
  const stopFrame = () => {
    if (frame !== null) window.cancelAnimationFrame(frame)
    frame = null
  }
  const releaseGesture = () => {
    const id = gesture?.id
    gesture = null
    removeGestureListeners()
    if (id != null && element.hasPointerCapture(id)) element.releasePointerCapture(id)
  }
  const paint = value => {
    offset = value
    element.classList.add('receiving-swipe-active')
    element.style.setProperty('--receiving-swipe-x', `${value}px`)
  }
  const cancel = () => {
    revision += 1
    if (gesture?.horizontal || Math.abs(offset) > 0) suppressClickUntil = Date.now() + 400
    releaseGesture()
    stopFrame()
    offset = 0
    prepared = false
    preparing = null
    running = false
    settling = false
    committing = false
    runType = null
    expectedType = null
    requestedType = null
    element.classList.remove('receiving-swipe-active')
    element.style.removeProperty('--receiving-swipe-x')
    clear()
  }
  const ensurePrepared = () => {
    if (prepared) return Promise.resolve(true)
    if (preparing) return preparing
    const token = revision
    runType = activeType()
    const pending = Promise.resolve(prepare()).then(geometry => {
      if (token !== revision || !enabled() || !geometry?.width) return false
      width = geometry.width
      prepared = true
      return true
    }).finally(() => { if (preparing === pending) preparing = null })
    preparing = pending
    return pending
  }

  let startStep
  const commit = async (type, token) => {
    committing = true
    await select(type)
    await nextTick()
    if (token !== revision) return
    if (!enabled() || activeType() !== type) { cancel(); return }
    // The real page takes over at the same reading anchor as its preview.
    // Complete the swap in this microtask turn, before another painted frame.
    committing = false
    settling = false
    running = false
    expectedType = null
    prepared = false
    runType = activeType()
    paint(0)
    clear()
    if (requestedType != null && requestedType !== activeType()) startStep()
    else cancel()
  }
  const settle = (target, type = null, velocity = 0) => {
    stopFrame()
    running = true
    settling = true
    expectedType = type
    const token = revision
    const complete = () => {
      paint(target)
      if (type == null) cancel()
      else commit(type, token)
    }
    if (reducedMotion() || Math.abs(offset - target) < 0.25) { complete(); return }
    const origin = offset
    const delta = origin - target
    const rate = 28
    // Critical damping preserves the release velocity without overshooting into
    // a fourth page. Its duration follows the remaining distance, not a timer.
    const limit = Math.abs(delta) * rate
    const speed = Math.max(-limit, Math.min(limit, velocity * 1000))
    const coefficient = speed + rate * delta
    const started = performance.now()
    const tick = now => {
      if (token !== revision) return
      if (!enabled()) { cancel(); return }
      if (reducedMotion()) { frame = null; complete(); return }
      const time = Math.max(0, now - started) / 1000
      const decay = Math.exp(-rate * time)
      const value = target + (delta + coefficient * time) * decay
      const currentSpeed = (speed - rate * coefficient * time) * decay
      paint(Math.max(-width, Math.min(width, value)))
      if ((Math.abs(value - target) < 0.35 && Math.abs(currentSpeed) < 8) || time > 0.65) {
        frame = null
        complete()
      } else frame = window.requestAnimationFrame(tick)
    }
    frame = window.requestAnimationFrame(tick)
  }
  startStep = async () => {
    if (running) return
    running = true
    paint(offset)
    const token = revision
    if (!await ensurePrepared()) { if (token === revision) cancel(); return }
    if (token !== revision) return
    const options = types()
    const current = options.indexOf(activeType())
    const goal = options.indexOf(requestedType)
    if (current < 0 || goal < 0 || goal === current) { cancel(); return }
    const direction = goal > current ? 1 : -1
    settle(-direction * width, options[current + direction])
  }
  const paintDrag = () => {
    if (!gesture?.horizontal || !prepared) return
    const options = types()
    const index = options.indexOf(gesture.type)
    const distance = gesture.origin + gesture.lastX - gesture.x
    const previous = options[index - 1] != null ? width : 0
    const next = options[index + 1] != null ? -width : 0
    // The first and last page have a firm boundary, with no empty neighbour.
    const value = Math.max(next, Math.min(previous, distance))
    if (reducedMotion()) offset = value
    else paint(value)
  }
  const queueDrag = () => {
    if (!prepared) return
    stopFrame()
    frame = window.requestAnimationFrame(() => { frame = null; paintDrag() })
  }
  const down = event => {
    if (event.pointerType !== 'touch') return
    if (!event.isPrimary) { cancel(); return }
    const surface = event.target.closest('.receiving-swipe-area, .receiving-grid-scroll')
    if (!enabled() || committing || !surface
      || event.target.closest('input, textarea, select, [contenteditable="true"]')) return
    const selection = window.getSelection()
    if (selection && !selection.isCollapsed && element.contains(selection.anchorNode)) return
    if (event.clientX < 24 || event.clientX > window.innerWidth - 24) return
    const resume = settling ? expectedType : null
    const token = revision
    stopFrame()
    running = false
    settling = false
    requestedType = null
    expectedType = null
    suppressClickUntil = Math.abs(offset) > 0 ? Date.now() + 400 : 0
    gesture = {
      id: event.pointerId,
      type: activeType(),
      x: event.clientX,
      y: event.clientY,
      lastX: event.clientX,
      lastTime: event.timeStamp,
      velocity: 0,
      origin: offset,
      horizontal: false,
      resume,
    }
    gestureListeners.forEach(([name, listener]) => window.addEventListener(name, listener, { passive: false }))
    ensurePrepared().then(ready => { if (ready && token === revision) queueDrag() })
  }
  const move = event => {
    const start = gesture
    if (!start || event.pointerId !== start.id) return
    if (!enabled() || activeType() !== start.type) { cancel(); return }
    const dx = event.clientX - start.x
    const dy = event.clientY - start.y
    if (!start.horizontal) {
      if (Math.max(Math.abs(dx), Math.abs(dy)) < 10) return
      if (Math.abs(dx) <= Math.abs(dy) * 1.3) {
        releaseGesture()
        if (Math.abs(offset) > 0) settle(0)
        else cancel()
        return
      }
      start.horizontal = true
      element.setPointerCapture(event.pointerId)
    }
    const elapsed = event.timeStamp - start.lastTime
    if (elapsed > 0) {
      const velocity = (event.clientX - start.lastX) / elapsed
      start.velocity = Math.max(-2.5, Math.min(2.5, velocity))
    }
    start.lastX = event.clientX
    start.lastTime = event.timeStamp
    if (event.cancelable) event.preventDefault()
    queueDrag()
  }
  const finish = async event => {
    const start = gesture
    if (!start || event.pointerId !== start.id) return
    const token = revision
    if (start.horizontal) { start.lastX = event.clientX; paintDrag() }
    releaseGesture()
    stopFrame()
    if (!start.horizontal && Math.abs(offset) < 0.25) { cancel(); return }
    suppressClickUntil = Date.now() + 400
    if (!await ensurePrepared() || token !== revision) return
    if (!start.horizontal) {
      requestedType = start.resume
      const direction = Math.sign(types().indexOf(start.resume) - types().indexOf(start.type))
      settle(start.resume == null ? 0 : -direction * width, start.resume)
      return
    }
    const options = types()
    const direction = offset < 0 ? 1 : -1
    const next = options[options.indexOf(start.type) + direction]
    const velocity = event.timeStamp - start.lastTime < 100 ? start.velocity : 0
    const projection = offset + velocity * 140
    const threshold = Math.min(100, Math.max(48, width * 0.25))
    const accepted = next != null && Math.abs(offset) >= 26 && projection * Math.sign(offset) > threshold
    requestedType = accepted ? next : null
    settle(accepted ? -direction * width : 0, requestedType, velocity)
  }
  const interrupted = event => {
    if (event.type === 'lostpointercapture' && event.target !== element) return
    if (!gesture || event.pointerId !== gesture.id) return
    const horizontal = gesture.horizontal || Math.abs(offset) > 0
    releaseGesture()
    if (horizontal) { suppressClickUntil = Date.now() + 400; settle(0) } else cancel()
  }
  const click = event => {
    if (event.detail !== 0 && Date.now() < suppressClickUntil && event.target.closest('.receiving-swipe-area, .receiving-grid-scroll')) {
      event.preventDefault()
      event.stopImmediatePropagation()
    }
  }
  gestureListeners.push(['pointermove', move], ['pointerup', finish], ['pointercancel', interrupted], ['lostpointercapture', interrupted])
  element.addEventListener('pointerdown', down, { passive: true })
  element.addEventListener('click', click, true)
  window.addEventListener('resize', cancel)
  window.addEventListener('blur', cancel)

  return {
    cancel,
    step(direction) {
      if (!enabled() || ![-1, 1].includes(direction)) return
      const options = types()
      const index = options.indexOf(requestedType ?? expectedType ?? activeType())
      const next = index < 0 ? null : options[index + direction]
      if (next == null) return
      if (gesture) cancel()
      requestedType = next
      if (!running) startStep()
    },
    sync() {
      if (!enabled() || (runType != null && activeType() !== runType && !(committing && activeType() === expectedType))) cancel()
    },
    destroy() {
      cancel()
      element.removeEventListener('pointerdown', down)
      element.removeEventListener('click', click, true)
      window.removeEventListener('resize', cancel)
      window.removeEventListener('blur', cancel)
    },
  }
}
